import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import cx from "classnames";
import { useCallback, useMemo, useRef, useState } from "react";
import { IoWarningOutline } from "react-icons/io5";

import { ARBITRUM, AVALANCHE, AVALANCHE_FUJI, getExplorerUrl } from "config/chains";
import { RebateDistributionType, TotalReferralsStats, useTiers } from "domain/referrals";
import { useAffiliateDistributions } from "domain/referrals/hooks/useAffiliateDistributions";
import { useMarketsInfoRequest } from "domain/synthetics/markets";
import { useAffiliateRewards } from "domain/synthetics/referrals/useAffiliateRewards";
import { getTotalClaimableAffiliateRewardsUsd } from "domain/synthetics/referrals/utils";
import { useTokensDataRequest } from "domain/synthetics/tokens/useTokensDataRequest";
import { convertToUsd } from "domain/synthetics/tokens/utils";
import { formatDate } from "lib/dates";
import { shortenAddress } from "lib/legacy";
import { formatBalanceAmount, formatNumber } from "lib/numbers";
import { userAnalytics } from "lib/userAnalytics";
import { ReferralCreateCodeEvent } from "lib/userAnalytics/types";
import useWallet from "lib/wallets/useWallet";
import { getNativeToken, getToken, getTokenBySymbolSafe } from "sdk/configs/tokens";

import Button from "components/Button/Button";
import Loader from "components/Common/Loader";
import ExternalLink from "components/ExternalLink/ExternalLink";
import { BottomTablePagination } from "components/Pagination/BottomTablePagination";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import { TableTd, TableTh, TableTheadTr, TableTr } from "components/Table/Table";
import { TableScrollFadeContainer } from "components/TableScrollFade/TableScrollFade";
import Tooltip from "components/Tooltip/Tooltip";

import { AffiliateCodeForm } from "./AddAffiliateCode";
import Modal from "../Modal/Modal";
import { ClaimAffiliatesModal } from "./ClaimAffiliatesModal/ClaimAffiliatesModal";
import EmptyMessage from "./EmptyMessage";
import { getSharePercentage, getUSDValue } from "./referralsHelper";
import usePagination, { DEFAULT_PAGE_SIZE } from "./usePagination";
import "./AffiliatesStats.scss";
import ReferralCodeCard from "./ReferralCodeCard";

import Tabs from "components/Tabs/Tabs";

import ReferralTable from "./ReferralTable";

type Props = {
  chainId: number;
  referralAccount?: string | null;
  referralsData?: TotalReferralsStats;
  handleCreateReferralCode: (code: string) => void;
  onReferralCodeCreated?: () => void;
  selectedTable: string;
  onSelectedTableChange: (value: string) => void;
};

function AffiliatesStats({
  chainId,
  referralAccount,
  referralsData,
  handleCreateReferralCode,
  onReferralCodeCreated,
  selectedTable,
  onSelectedTableChange,
}: Props) {
  useLingui();
  const { signer, account } = useWallet();
  const [isAddReferralCodeModalOpen, setIsAddReferralCodeModalOpen] = useState(false);
  const addNewModalRef = useRef<HTMLDivElement>(null);
  const { marketsInfoData } = useMarketsInfoRequest(chainId);
  const { tokensData } = useTokensDataRequest(chainId);
  const { affiliateRewardsData } = useAffiliateRewards(chainId);

  const esGmxAddress = getTokenBySymbolSafe(chainId, "esGMX")?.address;

  const [isClaiming, setIsClaiming] = useState(false);
  const open = useCallback(() => {
    userAnalytics.pushEvent<ReferralCreateCodeEvent>({
      event: "ReferralCodeAction",
      data: {
        action: "CreateCode",
      },
    });
    setIsAddReferralCodeModalOpen(true);
  }, []);
  const close = useCallback(() => setIsAddReferralCodeModalOpen(false), []);

  const { chains } = referralsData || {};
  const {
    [chainId]: currentReferralsData,
    [ARBITRUM]: arbitrumData,
    [AVALANCHE]: avalancheData,
    [AVALANCHE_FUJI]: fujiData,
  } = chains || {};

  const { affiliateDistributions, affiliateTierInfo, affiliateReferralCodesStats } = currentReferralsData || {};
  const { affiliateDistributions: fetchedAffiliateDistributions, isLoading: isLoadingRewardsHistory } =
    useAffiliateDistributions(chainId, referralAccount, selectedTable === "rewards-history");
  const rebateDistributions =
    fetchedAffiliateDistributions.length > 0 ? fetchedAffiliateDistributions : affiliateDistributions || [];

  const {
    currentPage: currentRebatePage,
    getCurrentData: getCurrentRebateData,
    setCurrentPage: setCurrentRebatePage,
    pageCount: rebatePageCount,
  } = usePagination("Rebates", rebateDistributions);

  const currentRebateData = getCurrentRebateData();
  const {
    currentPage: currentAffiliatesPage,
    getCurrentData: getCurrentAffiliatesData,
    setCurrentPage: setCurrentAffiliatesPage,
    pageCount: affiliatesPageCount,
  } = usePagination("Affiliates", affiliateReferralCodesStats || []);

  const currentAffiliatesData = getCurrentAffiliatesData();
  const referralHistoryTabOptions = [
    { label: t`Referrals`, value: "referrals" },
    { label: t`Rewards History`, value: "rewards-history" },
  ];
  const tierId = affiliateTierInfo?.tierId;
  const discountShare = affiliateTierInfo?.discountShare;
  const { totalRebate } = useTiers(signer, chainId, tierId);
  const currentRebatePercentage = getSharePercentage(tierId, BigInt(discountShare ?? 0n), totalRebate, true);

  const totalClaimableRewardsUsd = useMemo(() => {
    if (!affiliateRewardsData || !marketsInfoData) {
      return 0n;
    }

    return getTotalClaimableAffiliateRewardsUsd(marketsInfoData, affiliateRewardsData);
  }, [affiliateRewardsData, marketsInfoData]);

  const tokensDataByAddress = useMemo(() => {
    const map = new Map<string, { address?: string; prices?: { minPrice?: bigint } }>();
    Object.values(tokensData || {}).forEach((token) => {
      if (!token?.address) return;
      map.set(token.address.toLowerCase(), token);
    });
    return map;
  }, [tokensData]);

  return (
    <div className="referral-body-container">
      <div className="flex flex-col items-stretch gap-16 lg:flex-row xl:gap-24">
        <div className="flex-1 rounded-10 bg-gray-darker p-24">
          <p className="leading-none text-[20px] font-medium text-white">
            <Trans>Referral overview</Trans>
          </p>
          <div className="mt-23 grid grid-cols-2 gap-0 md:grid-cols-4">
            {[
              {
                label: t`Traders Referred`,
                value: formatNumber(currentReferralsData?.affiliateTotalStats?.registeredReferralsCount || 0, {
                  decimals: 0,
                  isForceDecimal: false,
                }),
                tooltip: t`Amount of traders you referred.`,
              },
              {
                label: t`Referrals Who Traded`,
                value: formatNumber(currentReferralsData?.affiliateTotalStats?.tradedReferralsCount || 0, {
                  decimals: 0,
                  isForceDecimal: false,
                }),
                tooltip: t`Amount of referred traders with at least one trade.`,
              },
              {
                label: t`Rewards Earned`,
                value: `$${getUSDValue(currentReferralsData?.affiliateTotalStats?.affiliateRebateUsd)}`,
                tooltip: t`Rewards earned by this account as an affiliate.`,
              },
              {
                label: t`Claimable Reward`,
                value: `$${getUSDValue(totalClaimableRewardsUsd, 2)}`,
                tooltip: t`Claim Reward from your referred Traders.`,
              },
            ].map((item, index) => {
              const isLastDesktop = index === 3;
              const isLeftMobile = index % 2 === 0;
              const isTopMobile = index < 2;

              return (
                <div
                  key={item.label}
                  className={cx("flex flex-col gap-12 px-0 py-14", {
                    "border-r border-gray-dark pr-24": isLeftMobile,
                    "pl-24": !isLeftMobile,
                    "border-b border-gray-dark pb-16": isTopMobile,
                    "md:border-b-0 md:pb-0": isTopMobile,
                    "md:border-r md:border-gray-dark md:pr-24": !isLastDesktop,
                    "md:border-r-0 md:pr-0": isLastDesktop,
                    "md:pl-0": index === 0,
                    "md:pl-24": index !== 0,
                  })}
                >
                  <div className="text-12 font-medium leading-[1.5] text-gray-lighter sm:text-14">
                    <Tooltip handle={item.label} position="bottom" renderContent={() => item.tooltip} />
                  </div>
                  <div className="text-18 sm:text-20 font-medium leading-[1.5] text-white">{item.value}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-20 flex justify-end">
            <Button variant="primary" disabled={totalClaimableRewardsUsd <= 0} onClick={() => setIsClaiming(true)}>
              <Trans>Claim Rewards</Trans>
            </Button>
          </div>
        </div>
        <ReferralCodeCard
          openGenerateCodeModal={open}
          currentRebatePercentage={currentRebatePercentage}
          affiliateReferralCodesStats={affiliateReferralCodesStats || []}
        />
      </div>
      {/* <div className="list"> */}
      <Modal
        className="Create-referral-modal"
        isVisible={isAddReferralCodeModalOpen}
        setIsVisible={close}
        label={t`Create Referral Code`}
        description={t`Create & customize referral code to start inviting frens`}
        onAfterOpen={() => addNewModalRef.current?.focus()}
      >
        <AffiliateCodeForm
          handleCreateReferralCode={handleCreateReferralCode}
          callAfterSuccess={() => {
            close();
            onReferralCodeCreated?.();
          }}
        />
      </Modal>

      {/* </div> */}
      <div className="mb-24 mt-18 rounded-12 bg-gray-darker px-20 py-16 md:mt-24">
        <Tabs
          options={referralHistoryTabOptions}
          selectedValue={selectedTable}
          onChange={onSelectedTableChange}
          variant="filled"
          className="w-max"
          regularOptionClassname="!min-w-[124px]"
        />
        {/* {
          selectedTable === 'referrals' && <Card
            slimHeader
            classNameHeader="!p-0 mb-4"
            className="mt-16"
            title={
              <div className="referral-table-header">
                <p className="title text-body-large">
                  <Trans>Referral Codes</Trans>{" "}
                  <span className="sub-title">
                    {affiliateTierInfo && t`Tier ${getTierIdDisplay(tierId)} (${currentRebatePercentage}% rebate)`}
                  </span>
                </p>
                <Button variant="tertiary" onClick={open}>
                  <FiPlus />{" "}
                  <span className="ml-small">
                    <Trans>Create</Trans>
                  </span>
                </Button>
              </div>
            }
            divider={false}
            bodyPadding={false}
          >
            <TableScrollFadeContainer>
              <table className="w-full">
                <thead>
                  <TableTheadTr bordered>
                    <TableTh scope="col">
                      <Trans>Referral Code</Trans>
                    </TableTh>
                    <TableTh scope="col">
                      <Trans>Total Volume</Trans>
                    </TableTh>
                    <TableTh scope="col">
                      <Trans>Traders Referred</Trans>
                    </TableTh>
                    <TableTh scope="col">
                      <Trans>Total Rebates</Trans>
                    </TableTh>
                  </TableTheadTr>
                </thead>
                <tbody>
                  {currentAffiliatesData.map((stat, index) => {
                    return (
                      <TableTr key={index} hoverable={false} bordered={false}>
                        <TableTd data-label={t`Referral Code`}>
                          <div className="table-referral-code">
                            <span className="referral-text ">{stat.referralCode}</span>
                            <div
                              onClick={() => {
                                trackCopyCode();
                                copyToClipboard(getReferralCodeTradeUrl(stat.referralCode));
                                helperToast.success(t`Referral link copied to your clipboard`);
                              }}
                              className="referral-code-icon"
                            >
                              <BiCopy />
                            </div>
                            <TrackingLink onClick={trackShareTwitter}>
                              <a
                                href={getTwitterShareUrl(stat.referralCode)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="referral-code-icon"
                              >
                                <FiTwitter />
                              </a>
                            </TrackingLink>
                            <ReferralCodeWarnings allOwnersOnOtherChains={stat?.allOwnersOnOtherChains} />
                          </div>
                        </TableTd>
                        <TableTd data-label={t`Total Volume`}>
                          <Tooltip
                            handle={`$${getUSDValue(stat.volume)}`}
                            position="bottom-start"
                            className="whitespace-nowrap"
                            renderContent={() => (
                              <>
                                <StatsTooltipRow label={t`Volume on V1`} value={getUSDValue(stat?.v1Data.volume)} />
                                <StatsTooltipRow label={t`Volume on V2`} value={getUSDValue(stat?.v2Data.volume)} />
                              </>
                            )}
                          />
                        </TableTd>
                        <TableTd data-label={t`Traders Referred`}>{stat.registeredReferralsCount}</TableTd>
                        <TableTd data-label={t`Total Rebates`}>
                          <Tooltip
                            handle={`$${getUSDValue(stat.affiliateRebateUsd)}`}
                            position="bottom-start"
                            className="whitespace-nowrap"
                            renderContent={() => (
                              <>
                                <StatsTooltipRow
                                  label={t`Rebates on V1`}
                                  value={getUSDValue(stat.v1Data.affiliateRebateUsd)}
                                />
                                <StatsTooltipRow
                                  label={t`Rebates on V2`}
                                  value={getUSDValue(stat.v2Data.affiliateRebateUsd)}
                                />
                              </>
                            )}
                          />
                        </TableTd>
                      </TableTr>
                    );
                  })}
                  {currentAffiliatesData.length > 0 && currentAffiliatesData.length < DEFAULT_PAGE_SIZE && (
                    // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
                    <tr style={{ height: 43 * (DEFAULT_PAGE_SIZE - currentAffiliatesData.length) }}></tr>
                  )}
                </tbody>
              </table>
            </TableScrollFadeContainer>
            <BottomTablePagination
              page={currentAffiliatesPage}
              pageCount={affiliatesPageCount}
              onPageChange={setCurrentAffiliatesPage}
            />
          </Card>
        } */}
        {selectedTable === "referrals" && <ReferralTable referrals={currentReferralsData?.joinedReferrals} />}
        {selectedTable === "rewards-history" && (
          <>
            {isLoadingRewardsHistory ? (
              <div className="pt-16">
                <Loader />
              </div>
            ) : currentRebateData.length > 0 ? (
              <div className="pt-16">
                {/* <Card
                  title={
                    <span className="text-body-large">
                      <Trans>Rebates Distribution History</Trans>
                    </span>
                  }
                  classNameHeader="!p-0"
                  tooltipText={t`V1 Rebates and V1/V2 esGMX are airdropped weekly. V2 Rebates are claimed manually.`}
                  bodyPadding={false}
                  divider={false}
                >
                  
                </Card> */}
                <TableScrollFadeContainer>
                  <table className="w-full min-w-max">
                    <thead>
                      <TableTheadTr bordered>
                        <TableTh scope="col">
                          <Trans>Date</Trans>
                        </TableTh>
                        <TableTh scope="col">
                          <Trans>Type</Trans>
                        </TableTh>
                        <TableTh scope="col">
                          <Trans>Amount</Trans>
                        </TableTh>
                        <TableTh scope="col">
                          <Trans>Transaction</Trans>
                        </TableTh>
                      </TableTheadTr>
                    </thead>
                    <tbody>
                      {/* {[
                        {
                          typeId: RebateDistributionType.Rebate,
                          receiver: "0x1234567890abcdef1234567890abcdef12345678",
                          markets: ["0xMarket1", "0xMarket2"],
                          tokens: ["0xToken1", "0xToken2"],
                          amounts: [BigInt(1000000000000000000), BigInt(2000000000000000000)], // 1 and 2 tokens (assuming 18 decimals)
                          amountsInUsd: [BigInt(1500000000000000000), BigInt(3000000000000000000)], // $1.5 and $3.0
                          timestamp: Math.floor(Date.now() / 1000),
                          transactionHash: "0xabcdefabcdefabcdefabcdefabcdefabcdefabcdefabcdefabcdefabcdefabcdef",
                          id: "mock1",
                        },
                        {
                          typeId: RebateDistributionType.Claim,
                          receiver: "0xabcdefabcdefabcdefabcdefabcdefabcdefabcdef",
                          markets: ["0xMarket3"],
                          tokens: ["0xToken3"],
                          amounts: [BigInt(500000000000000000)],
                          amountsInUsd: [BigInt(750000000000000000)],
                          timestamp: Math.floor(Date.now() / 1000) - 86400,
                          transactionHash: "0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef",
                          id: "mock2",
                        },
                      ].map((rebate, index) => { */}
                      {currentRebateData?.map?.((rebate, index) => {
                        let rebateType = "-";

                        if (rebate.typeId === RebateDistributionType.Rebate) {
                          if (esGmxAddress && rebate.tokens[0] === esGmxAddress) {
                            rebateType = t`V1 esGMX`;
                          } else {
                            rebateType = t`V1 Airdrop`;
                          }
                        } else if (rebate.typeId === RebateDistributionType.Claim) {
                          rebateType = t`V2 Claim`;
                        }

                        const tokenMetaByAddress: Record<
                          string,
                          { symbol: string; decimals: number; isStable?: boolean }
                        > = {};
                        const amountsByTokens: { [address: string]: bigint } = {};
                        const historicalUsdByTokens: { [address: string]: bigint } = {};

                        rebate.tokens.forEach((tokenAddress, i) => {
                          const rawTokenAddress = String(tokenAddress ?? "").toLowerCase();
                          let token: ReturnType<typeof getToken> | ReturnType<typeof getNativeToken> | undefined;

                          try {
                            token = getToken(chainId, tokenAddress);
                          } catch (_error) {
                            token = undefined;
                          }

                          const tokenKey = (token?.address ?? rawTokenAddress).toLowerCase();
                          tokenMetaByAddress[tokenKey] = token
                            ? { symbol: token.symbol, decimals: token.decimals, isStable: token.isStable }
                            : { symbol: shortenAddress(tokenAddress, 4), decimals: 18, isStable: false };

                          amountsByTokens[tokenKey] = (amountsByTokens[tokenKey] ?? 0n) + (rebate.amounts[i] ?? 0n);
                          historicalUsdByTokens[tokenKey] =
                            (historicalUsdByTokens[tokenKey] ?? 0n) + (rebate.amountsInUsd[i] ?? 0n);
                        });

                        const tokensWithoutPrices = new Set<string>();
                        const amountParts = Object.keys(amountsByTokens).map((tokenAddress) => {
                          const tokenMeta = tokenMetaByAddress[tokenAddress];
                          const tokenAmount = amountsByTokens[tokenAddress] ?? 0n;
                          const historicalUsd = historicalUsdByTokens[tokenAddress] ?? 0n;
                          const liveTokenData = tokensDataByAddress.get(tokenAddress.toLowerCase());
                          const estimatedUsd =
                            historicalUsd > 0n
                              ? historicalUsd
                              : liveTokenData
                                ? convertToUsd(tokenAmount, tokenMeta.decimals, liveTokenData.prices?.minPrice)
                                : undefined;

                          const finalUsd = estimatedUsd !== undefined && estimatedUsd > 0n ? estimatedUsd : undefined;
                          if (finalUsd === undefined && tokenAmount > 0n) {
                            tokensWithoutPrices.add(tokenAddress);
                          }

                          const amountText = `${formatBalanceAmount(tokenAmount, tokenMeta.decimals, undefined, {
                            isStable: tokenMeta.isStable,
                          })} ${tokenMeta.symbol}`;
                          const usdText = finalUsd !== undefined ? `$${getUSDValue(finalUsd)}` : "$--";

                          return {
                            tokenAddress,
                            tokenMeta,
                            tokenAmount,
                            usdValue: finalUsd,
                            text: `${amountText} (${usdText})`,
                          };
                        });

                        const amountLabel = amountParts.map((part) => part.text).join(", ") || "$0.00";
                        const explorerURL = getExplorerUrl(chainId);
                        return (
                          <TableTr key={index} hoverable={false} bordered={false}>
                            <TableTd data-label={t`Date`}>{formatDate(rebate.timestamp)}</TableTd>
                            <TableTd data-label={t`Type`}>{rebateType}</TableTd>
                            <TableTd data-label={t`Amount`}>
                              <Tooltip
                                className="whitespace-nowrap"
                                handle={
                                  <div className="Rebate-amount-value">
                                    {tokensWithoutPrices.size > 0 && (
                                      <>
                                        <IoWarningOutline color="#ffba0e" size={16} />
                                        &nbsp;
                                      </>
                                    )}
                                    {amountLabel}
                                  </div>
                                }
                                renderContent={() => (
                                  <>
                                    {tokensWithoutPrices.size > 0 && (
                                      <>
                                        <Trans>
                                          USD Value may not be accurate since the data does not contain prices for{" "}
                                          {Array.from(tokensWithoutPrices)
                                            .map(
                                              (address) =>
                                                tokenMetaByAddress[address]?.symbol ?? shortenAddress(address, 4)
                                            )
                                            .join(", ")}
                                        </Trans>
                                        <br />
                                        <br />
                                      </>
                                    )}
                                    {amountParts.map((part) => {
                                      const token = part.tokenMeta;

                                      return (
                                        <StatsTooltipRow
                                          key={part.tokenAddress}
                                          showDollar={false}
                                          label={token.symbol}
                                          value={`${formatBalanceAmount(part.tokenAmount, token.decimals, undefined, {
                                            isStable: token.isStable,
                                          })} (${part.usdValue ? `$${getUSDValue(part.usdValue)}` : "$--"})`}
                                        />
                                      );
                                    })}
                                  </>
                                )}
                              />
                            </TableTd>
                            <TableTd data-label={t`Transaction`}>
                              <ExternalLink href={explorerURL + `tx/${rebate.transactionHash}`}>
                                {shortenAddress(rebate.transactionHash, 13)}
                              </ExternalLink>
                            </TableTd>
                          </TableTr>
                        );
                      })}
                      {currentRebateData.length < DEFAULT_PAGE_SIZE && (
                        // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
                        <tr style={{ height: 42.5 * (DEFAULT_PAGE_SIZE - currentRebateData.length) }}></tr>
                      )}
                    </tbody>
                  </table>
                </TableScrollFadeContainer>
                <BottomTablePagination
                  page={currentRebatePage}
                  pageCount={rebatePageCount}
                  onPageChange={setCurrentRebatePage}
                />
              </div>
            ) : (
              <EmptyMessage
                tooltipText={t`Rebates are airdropped weekly.`}
                message={t`No rebates distribution history yet.`}
                className="!border-none !p-0"
              />
            )}
          </>
        )}
      </div>

      {isClaiming && <ClaimAffiliatesModal onClose={() => setIsClaiming(false)} />}
    </div>
  );
}

export default AffiliatesStats;
